import _ from 'lodash';
import { getCurrencyType } from '../lib';
/**
 * A function to format number values
 * @param value the number that will be formatted.
 * @param options the formatting options:
 *  currency: if true, the number will be prefixed with a '$' and formatted with 2 decimal places.
 *  roundDecimal: a number detailing how many decimal places the formatted number will have.
 *  format: if true, the number will be formatted to the users locale.
 *  integer: if true, the number will have no decimal places
 *  locale: the locale of the value (ex: 'en-US')
 *  prefix: the string that prefixes the number (ex: '+')
 */
export const formatNumber = (value, options) => {
    // Destructure the options object, providing defaults if not passed in.
    const { currency, // Format value as currency if true or specify a specific currency code
    roundDecimal, // Number of decimal places to round to
    format, // Enable custom format options
    integer, // Indicates if the value should be formatted as an integer
    locale, // Locale for number formatting, defaults to user's locale if not provided
    percent, // Format value as a percentage if true
    prefix, // String to prepend to the formatted number
    greeks, // Indicates if the number is a Greek (financial option term)
    isStrikeField, // Specific formatting for strike prices
    precisionOverride, // Allows for overriding precision with custom min/max fraction digits
    isShareField, } = (options || {});
    // Convert the value to a number if it's not already, removing currency symbols and commas.
    const valueAsNumber = typeof value === 'number' ? value : !_.isNil(value) ? parseFloat(value.replace(/[$,]+/g, '')) : 0;
    // If precisionOverride is provided, apply the custom precision settings and return formatted string.
    if (precisionOverride) {
        let precision = valueAsNumber.toLocaleString(locale, {
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        });
        return `${prefix || ''}${precision}`; // Prepend the prefix if any, and return the precision formatted number
    }
    // Set decimal precision, defaulting to 2 decimal places unless roundDecimal or integer flag is set.
    const dec = _.isNumber(roundDecimal) ? roundDecimal : integer ? 0 : 2;
    // Determine the locale to use for formatting; fall back to the user's locale if not provided.
    const userLocale = locale || navigator.language;
    // Define options for number formatting based on the presence of currency or custom format.
    const localeOptions = currency
        ? { style: 'currency', currency: _.isBoolean(currency) ? getCurrencyType(locale) : currency } // Format as currency
        : format
            ? { minimumFractionDigits: dec, maximumFractionDigits: 2 } // Set fractional digits if formatting is required
            : undefined;
    // Round the value to the specified decimal places.
    const roundedValue = _.round(valueAsNumber, dec);
    // Format the number based on the localeOptions or as a percentage if percent is true, otherwise use fixed decimals.
    let num = localeOptions
        ? roundedValue.toLocaleString(userLocale, localeOptions) // Apply locale-based formatting
        : percent
            ? formatPercent(roundedValue) // Format as a percentage
            : roundedValue.toFixed(dec); // Default to fixed decimal formatting
    // Handle special formatting for Greeks (e.g., Implied Volatility), which are percentages but shown as decimals.
    if (greeks && percent) {
        num = formatPercent(valueAsNumber * 100, true); // Multiply value by 100 for Greeks when percent is true
    }
    // Handle strike fields, formatting as integers if whole numbers, or with 2 decimals if not.
    if (isStrikeField) {
        num = valueAsNumber % 1 === 0 ? valueAsNumber.toFixed(0) : valueAsNumber.toFixed(2);
    }
    // Special formatting for share fields: show up to 5 decimal places, but only if significant digits are present
    if (isShareField) {
        num = valueAsNumber.toLocaleString(userLocale, {
            minimumFractionDigits: valueAsNumber % 1 !== 0 ? 1 : 0,
            maximumFractionDigits: 5,
        });
    }
    // Return the final formatted number, prepended by any prefix if provided.
    return `${prefix || ''}${num}`;
};
export const formatPercent = (value, isGreek) => {
    // if isGreek, we want to format the percentage in the format of 0.00%
    if (isGreek) {
        return `${value.toFixed(2)}%`;
    }
    // if the value is greater than 10, we want to round it to the nearest integer
    // otherwise, we want to round it to 2 decimal places
    const num = Math.abs(value) > 10 ? (value > 0 ? _.ceil(value) : _.floor(value)) : value.toFixed(2);
    return `${num}%`;
};
// taken from: https://stackoverflow.com/a/45309230
export const parseNumber = (value, locale) => {
    const userLocale = locale || navigator.language;
    // format a known value using Intl.NumberFormat for user's locale...
    const formatExample = Intl.NumberFormat(userLocale).format(1.1);
    // ... then use known formatted value to determine decimal separator...
    const decimalSeparator = formatExample.charAt(1);
    // ... then build regex to remove everything except number values and decimal separator
    const formatRegex = new RegExp(`[^-+0-9${decimalSeparator}]`, 'g');
    const normalized = value.replace(formatRegex, '').replace(decimalSeparator, '.');
    return parseFloat(normalized);
};
//# sourceMappingURL=number.js.map